import React, {useEffect, useRef} from 'react'
import {TaskEditorLayout} from './TaskEditorLayout'
import {URLS} from '../../constants/urls'
import {useDispatch, useSelector} from 'react-redux'
import {IReduxState} from '../../redux/types/IReduxState'
import {useParams} from 'react-router'
import {taskActions} from './redux/actions'
import {TaskHeader} from '../header/TaskHeader'
import {TaskDto, TaskListItemDto, UserDto} from 'learndb-share'
import {getStructureByWindowStyle} from './utils'
import {TaskEditorDrawer} from './components/drawer/TaskEditorDrawer'

const TaskLayout: React.FC = () => {
  const taskDispatch = useDispatch()

  const {taskId} = useParams()

  const task = useSelector<IReduxState, TaskDto>((state) => state.task?.task)
  const taskError = useSelector<IReduxState, any | undefined>((state) => state.task?.taskError)
  const themeTasks = useSelector<IReduxState, TaskListItemDto[]>((state) => state.task?.themeTasks)
  const schemaDescription = useSelector<IReduxState, any | undefined>((state) => state.task?.schemaDescription)
  const user = useSelector<IReduxState, UserDto | undefined>((state) => state.auth?.user)
  const userAnonymous = useSelector<IReduxState, UserDto | undefined>((state) => state.auth?.userAnonymous)
  const token = useSelector<IReduxState, any | undefined>((state) => state.auth?.token)

  const isAuth = !!(token || userAnonymous)

  const containerRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

  const initWindowStyle = (): void => {
    let windowStyle = 0
    if (user && user.setting) {
      windowStyle = user.setting.windowStyle
    } else if (userAnonymous && userAnonymous.setting) {
      windowStyle = userAnonymous.setting.windowStyle
    }
    taskDispatch(
      taskActions.setState({
        ...getStructureByWindowStyle(windowStyle),
      }),
    )
  }

  const getTask = (newTaskId): void => {
    taskDispatch(taskActions.getTask(newTaskId))
  }

  const getThemeTasks = (newTaskId): void => {
    taskDispatch(taskActions.getThemeTasks(newTaskId))
  }

  const getSchemaDescription = (): void => {
    taskDispatch(taskActions.getSchemaDescription())
  }

  const isInitialized = (): boolean => {
    return !!task && !!themeTasks && !!schemaDescription
  }

  const setHeight = (): void => {
    if (isInitialized()) {
      taskDispatch(taskActions.setContainerHeight(containerRef?.current?.clientHeight || 0))
    }
  }

  const onResize = (): void => {
    setHeight()
  }

  useEffect(() => {
    if (isAuth) {
      getTask(taskId)
      initWindowStyle()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isAuth) {
      if (task) {
        getThemeTasks(taskId)
        getSchemaDescription()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, task])

  useEffect(() => {
    window.addEventListener('resize', onResize)

    return () => {
      taskDispatch(taskActions.clearState())
      window.removeEventListener('resize', onResize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (taskError?.status === 403) {
    window.location.replace(URLS.payment)
  }

  if (isInitialized()) {
    return (
      <div className={'task-layout'}>
        <TaskHeader isNewYear={false} />
        <div className={'task-editor-content'} ref={containerRef}>
          <TaskEditorDrawer />
          <TaskEditorLayout />
        </div>
      </div>
    )
  } else {
    if (!isAuth) {
      window.location.replace(URLS.tasks + '/' + taskId + '/unauthorized')
    } else {
      return null
    }
  }
}

export {TaskLayout}
